import * as React from "react";

import {
  Tags,
  withLoadingSpinner,
  withCategoryNotFoundHandler,
  withGraphqlErrorFlags,
  withShowFlag,
  FlagProps,
  GrayText,
  InlineBlockDiv,
  Link,
  ChildProps,
  compose,
  graphql,
  PageLayout
} from "@atlassiansox/microscopekit";
import { EditableText } from "../../../palette/editable/editable-text";
import { PropertyLabel } from "./property-label";
import categoryOverviewQuery from "../models/category-overview.query.graphql";
import styled from "styled-components";
import moment from "moment";
import { userUrl } from "../../../core/routes";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { categoryOverview } from "../models/__generated__/categoryOverview";

const Page = require("@atlaskit/page").default;
const { Grid, GridColumn } = require("@atlaskit/page");
const { DynamicTableStateless } = require("@atlaskit/dynamic-table");

type CategoryOverviewProps = RouteComponentProps<any> &
  ChildProps<{ id: string }, categoryOverview>;

const InlinePropertyLabel = styled(PropertyLabel)`
  display: inline-block;
  width: inherit;
`;

const perPage = 20;

export class CategoryOverviewView extends React.Component<
  FlagProps & CategoryOverviewProps
> {
  // ref: http://blog.sodhanalibrary.com/2016/08/detect-when-user-scrolls-to-bottom-of.html#.Xmal9pNKjRY
  handleScroll = () => {
    const body = document.body;
    const html = document.documentElement;
    const windowHeight =
      "innerHeight" in window ? window.innerHeight : html.offsetHeight;
    const docHeight = Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight
    );
    const windowBottom = windowHeight + window.pageYOffset;
    if (windowBottom >= docHeight) {
      this.getServices();
    }
  };
  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);
  }
  componentWillUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  }

  render() {
    return (
      <PageLayout title="Overview">
        <Page>
          <br />
          {this.renderChildren()}
          <br />
          <h4>Services</h4>
          <Grid>
            <GridColumn>{this.renderServicesTable()}</GridColumn>
          </Grid>
          <br />
          {this.renderMetadata()}
        </Page>
      </PageLayout>
    );
  }

  renderChildren = () => {
    const cat = this.props.data!.category!;
    return (
      <Grid>
        <GridColumn>
          <h4>Subcategories</h4>
          <Tags
            tags={cat.children.map(c => ({
              label: c.name,
              value: c.displayName || c.name,
              isSelected: true,
              link:
                "/categories/" +
                encodeURIComponent(c.id) +
                (this.props.location.search ? this.props.location.search : "")
            }))}
            color={"greyLight"}
            emptyMessage="No subcategories."
          />
        </GridColumn>
      </Grid>
    );
  };

  // Get next page of services
  getServices = async () => {
    try {
      if (!this.props.data!.category!.services) {
        return null;
      } else {
        const previousServices = this.props.data!.category!.services;
        const previousEdges = previousServices.edges;
        const previousCursor = previousEdges![previousEdges!.length - 1]!
          .cursor;
        if (previousServices.pageInfo.hasNextPage) {
          const result = await this.props.data!.fetchMore({
            variables: {
              first: perPage,
              after: previousCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              if (!fetchMoreResult!.category!.services) {
                return previousResult;
              }
              const { edges, pageInfo } = fetchMoreResult!.category!.services;
              const category = fetchMoreResult!.category!;
              return edges && edges.length
                ? {
                    category: {
                      ...category,
                      services: {
                        __typename: previousResult.category!.services!
                          .__typename,
                        edges: [
                          ...previousResult.category!.services!.edges,
                          ...edges
                        ],
                        pageInfo
                      }
                    }
                  }
                : previousResult;
            }
          });
          return result;
        }
      }
    } catch (e) {
      this.props.showFlag({
        type: "error",
        title: "Error",
        message: e.toString()
      });
      return e;
    }
  };

  renderServicesTable = () => {
    const cat = this.props.data!.category!;
    if (!cat.services || !cat.services.edges) {
      return <GrayText bold>No Services.</GrayText>;
    }
    const isLoading = this.props.data!.networkStatus == 3 ? true : false;
    return (
      <DynamicTableStateless
        isLoading={isLoading}
        key="key"
        head={{
          cells: [
            { key: "ServiceHeader", content: "Service" },
            { key: "OwnerHeader", content: "Owner" },
            { key: "TierHeader", content: "Service Tier" }
          ]
        }}
        rows={cat.services.edges.map(s => ({
          cells: [
            {
              key: s!.node!.id,
              content: (
                <Link to={"/services/" + encodeURIComponent(s!.node!.id)}>
                  {s!.node!.id}
                </Link>
              )
            },
            {
              key: s!.node!.metadata!.owner.id,
              content: (
                <Link to={userUrl(s!.node!.metadata!.owner.id)}>
                  {s!.node!.metadata!.owner.id}
                </Link>
              )
            },
            {
              key: s!.node!.id + s!.node!.metadata!.sla.serviceTier,
              content: s!.node!.metadata!.sla.serviceTier.toString()
            }
          ]
        }))}
      />
    );
  };

  renderMetadata = () => {
    const cat = this.props.data!.category!;
    if (!cat?.changeMetadata?.createdOn) {
      return null;
    }
    return (
      <Grid>
        <GridColumn>
          {cat?.changeMetadata?.createdOn &&
            this.renderTextRow(
              "Created On",
              moment(cat.changeMetadata.createdOn).fromNow(),
              "",
              "",
              false
            )}
          {cat?.changeMetadata?.modifiedOn &&
            this.renderTextRow(
              "Modified On",
              moment(cat.changeMetadata.modifiedOn!).fromNow(),
              "",
              "",
              false
            )}
          {cat?.changeMetadata?.modifiedBy?.id &&
            this.renderTextRow(
              "Modified By",
              cat.changeMetadata.modifiedBy?.id!,
              cat.changeMetadata.modifiedBy?.id!,
              userUrl(cat.changeMetadata.modifiedBy?.id!),
              true
            )}
        </GridColumn>
      </Grid>
    );
  };

  renderTextRow = (
    title: string,
    value: string,
    editValue?: string,
    linkValue?: string,
    isExternal?: boolean
  ) => (
    <div>
      <InlinePropertyLabel>
        {title + ":"}
        &nbsp;
      </InlinePropertyLabel>
      <InlineBlockDiv>
        <EditableText
          value={value}
          editValue={editValue}
          isLink={!!linkValue}
          linkValue={linkValue}
          isExternal={isExternal}
        />
      </InlineBlockDiv>
    </div>
  );
}

export const CategoryOverview = compose(
  graphql(categoryOverviewQuery, {
    options: (props: CategoryOverviewProps) => ({
      errorPolicy: "all",
      variables: {
        id: props.id,
        first: perPage * 2
      },
      notifyOnNetworkStatusChange: true
    })
  }),
  withLoadingSpinner,
  withShowFlag,
  withGraphqlErrorFlags,
  withCategoryNotFoundHandler,
  withRouter
)(CategoryOverviewView);
